/**
 * MainPanel component. Use Component list as line.
 *
 * @author Roman Stoian <r.stoyan@infomir.com>.
 */

/* eslint no-path-concat: 0 */
/* eslint complexity: 0 */
/* eslint max-statements: 0 */
/* eslint max-lines-per-function: 0 */

'use strict';

var Component = require('stb-component'),
    ItemList  = require('./item.list'),
    keys      = require('stb-keys');


/**
 * MainPanel implementation.
 *
 * Each data item can be either a primitive value or an object with these fields:
 *
 * @constructor
 * @extends Component
 *
 * @param {Object}   [config={}] - init parameters (all inherited from the parent)
 * @param {Array}    [config.data=[]] - component data to visualize
 * @param {Function} [config.render] - method to build each grid cell content
 * @param {Function} [config.navigate] - method to move focus according to pressed keys
 * @param {number}   [config.size=3] - amount of visible items on a page
 * @param {number}   [config.bufferSize=1] - amount of buffered items
 * @param {number}   [config.focusIndex] - list item index to make item focused (move view window to this position)
 * @param {boolean}  [config.hasEmptyItems=true] - need add empty items in before and after list data
 */
function MainPanel ( config ) {
    // current execution context
    var self = this;

    // sanitize
    config = config || {};

    if ( DEVELOP ) {
        if ( typeof config !== 'object' ) {
            throw new Error(__filename + ': wrong config type');
        }
        // init parameters checks
        if ( 'className' in config && (!config.className || typeof config.className !== 'string') ) {
            throw new Error(__filename + ': wrong or empty config.className');
        }
    }

    /**
     * Link to the currently focused DOM element.
     *
     * @type {Element}
     */
    this.focusLine = null;

    /**
     * Component data to visualize.
     *
     * @type {Array}
     */
    this.data = [];

    this.bufferBefore = [];
    this.mainList = [];
    this.bufferAfter = [];

    /**
     * Amount of visible items on a page.
     *
     * @type {number}
     */
    this.size = 3;
    this.bufferSize = 1;
    this.hasEmptyItems = config.hasEmptyItems || true;

    this.dataReady = false;

    // /**
    //  * Allow or not to jump to the opposite side of a list when there is nowhere to go next.
    //  *
    //  * @type {boolean}
    //  */
    // this.cycle = false;


    // config.className = config.className || '';

    //config.className += ' theme-main';

    config.$body = document.createElement('div');
    config.$body.className = 'body';

    // parent constructor call
    Component.call(this, config);

    this.$node.appendChild(this.$body);

    this.$noData = document.createElement('div');
    this.$noData.className = 'noData hidden';
    this.$node.appendChild(this.$noData);

    // component setup
    this.init(config);
    this.inFocus = false;

    this.addListener('focus', function () {
        if ( self.focusLine ) {
            self.focusLine.focus();
        }
        self.inFocus = true;
    });

    this.addListener('blur', function () {
        self.inFocus = false;
    });
}

// inheritance
MainPanel.prototype = Object.create(Component.prototype);
MainPanel.prototype.constructor = MainPanel;

// set component name
MainPanel.prototype.name = 'mag-component-grid';

/**
 * Add a new component as a first child.
 *
 * @param {...Component} [child] - variable number of elements to append
 *
 * @example
 * panel.insertBefore(
 *     new Button( ... ),
 *     new Button( ... )
 * );
 */
MainPanel.prototype.insertBefore = function ( child ) {
    var index;

    // walk through all the given elements
    for ( index = 0; index < arguments.length; index++ ) {
        child = arguments[index];

        if ( DEVELOP ) {
            if ( !(child instanceof Component) ) { throw new Error(__filename + ': wrong child type'); }
        }

        // apply
        this.children.unshift(child);
        child.parent = this;

        // correct DOM parent/child connection if necessary
        if ( child.$node && child.$node.parentNode === null ) {
            this.$body.insertBefore(child.$node, this.$body.firstChild);
        }

        debug.info('add component ' + child.name + '#' + child.id + ' to ' + this.name + '#' + this.id, null, {
            tags: ['add', 'component', this.name, this.id, child.name, child.id]
        });

        // there are some listeners
        if ( this.events['add'] ) {
            /**
             * A child component is added.
             *
             * @event module:stb/component~Component#add
             *
             * @type {Object}
             * @property {Component} item new component added
             */
            this.emit('add', {item: child});
        }

        //debug.log('component ' + this.name + '#' + this.id + ' new child: ' + child.name + '#' + child.id);
    }
};

/**
 * Init or re-init of the component inner structures and HTML.
 *
 * @param {Object} config - init parameters (subset of constructor config params)
 */
MainPanel.prototype.init = function ( config ) {
    var self     = this;

    if ( DEVELOP ) {
        if ( arguments.length !== 1 ) {
            throw new Error(__filename + ': wrong arguments number');
        }
        if ( typeof config !== 'object' ) {
            throw new Error(__filename + ': wrong config type');
        }
    }


    // custom render method
    if ( config.render ) {
        if ( DEVELOP ) {
            if ( typeof config.render !== 'function' ) {
                throw new Error(__filename + ': wrong config.render type');
            }
        }

        // apply
        this.renderItem = config.render;
    }

    // list items amount on page
    if ( config.size ) {
        if ( DEVELOP ) {
            if ( +config.size !== config.size ) {
                throw new Error(__filename + ': config.size must be a number');
            }
            if ( config.size <= 0 ) {
                throw new Error(__filename + ': config.size should be positive');
            }
        }

        // apply
        this.size = config.size;
    }

    if ( config.bufferSize ) {
        if ( DEVELOP ) {
            if ( +config.bufferSize !== config.size ) {
                throw new Error(__filename + ': config.size must be a number');
            }
            if ( config.bufferSize <= 0 ) {
                throw new Error(__filename + ': config.size should be positive');
            }
        }

        // apply
        this.bufferSize = config.bufferSize;
    }

    if ( config.events ) {
        // apply all given events
        Object.keys(config.events).forEach(function ( name ) {
            self.events[name] = null;
            self.addListener(name, config.events[name]);
        });
    }

    this.focusPosition = config.focusPosition || Math.ceil(this.size / 2);

    this.startIndex = 0;

    this.setData(config);
};

MainPanel.prototype.createItem = function ( data, index ) {
    var item;

    if ( data instanceof Component ) {
        item = data;
    } else {
        data.propagate = true;
        item = new ItemList(data);
        data.listLink = item;
        item.query = data;
        item.addListener('remove', function () {
            data.listLink = null;
        });
    }
    item.index = index;
    item.hide();
    item.parent = this;

    return item;
};

MainPanel.prototype.getItemData = function ( item, callback ) {
    if ( item.data && item.data.length ) {
        callback(item.data);
    } else if ( item.getter ) {
        item.getter(function ( listData ) {
            item.data = listData;
            callback(item.data);
        });
    } else {
        callback();
    }
};


/**
 * Fill the given item with data.
 *
 * @param {Element} $item - item DOM link
 * @param {*} data - associated with this item data
 */
MainPanel.prototype.renderItemDefault = function ( $item, data ) {
    $item.innerText = data.value;
};


/**
 * Method to build each list item content.
 * Can be redefined to provide custom rendering.
 *
 * @type {Function}
 */
MainPanel.prototype.renderItem = MainPanel.prototype.renderItemDefault;


/**
 * List of all default event callbacks.
 *
 * @type {Object.<string, function>}
 */
MainPanel.prototype.defaultEvents = {
    /**
     * Default method to handle mouse wheel events.
     *
     * @param {Event} event - generated event
     */
    mousewheel: function ( event ) {
        // scrolling by X axis
        if ( event.wheelDeltaX ) {
            this.move(event.wheelDeltaY > 0 ? keys.up : keys.down);
        }
    },

    /**
     * Default method to handle keyboard keydown events.
     *
     * @param {Object} event - generated event
     */
    keydown: function ( event ) {
        if ( event.stop ) {

            return;
        }
        switch ( event.code ) {
            case keys.down:
            case keys.up:
                event.stop = true;
                // cursor move only on arrow keys
                this.move(event.code);
                break;
            case keys.enter:
                // there are some listeners
                event.stop = true;
                if ( this.events['click:item'] && this.focusLine ) {
                    // notify listeners
                    this.emit('click:item', {$item: this.focusLine, event: event});
                }
                break;
        }
    }
};


/**
 * Insert data and render inner structures and HTML.
 *
 * @param {Object} data - init parameters (subset of constructor config params)
 * @param {number} itemIndex - insert index
 */
MainPanel.prototype.insert = function ( data, itemIndex ) {
    var self = this,
        startIndex, item, line,
        continueIndex;

    function updateIndex ( start ) {
        var index;

        for ( index = 0; index < self.bufferBefore.length; index++ ) {
            self.bufferBefore[index].index = start;
            start++;
        }

        for ( index = 0; index < self.mainList.length; index++ ) {
            self.mainList[index].index = start;
            start++;
        }

        for ( index = 0; index < self.bufferAfter.length; index++ ) {
            self.bufferAfter[index].index = start;
            start++;
        }
    }

    if ( this.data.length === 0 || this.mainList.length === 0 ) {
        this.setData({data: data});

        return;
    }

    if ( this.focusLine ) {
        line = this.focusLine;
    } else {

        return;
    }

    if ( !itemIndex ) {
        itemIndex = 0;
    }
    if ( this.data.length > itemIndex ) {
        itemIndex = this.data.length;
    }

    this.data.splice(itemIndex, 0, data);

    if ( this.bufferBefore.length ) {
        startIndex = this.bufferBefore[0].index;
    } else {
        startIndex = this.mainList[0].index;
    }

    if ( this.mainList[0].index > itemIndex ) {
        if ( this.bufferBefore.length ) {
            if ( this.bufferBefore[0].index >= itemIndex ) {
                this.viewIndex++;
                updateIndex(++this.bufferBefore[0].index);

                return;
            }
            if ( this.bufferBefore[0].index < itemIndex ) {
                item = this.createItem(data, itemIndex);
                continueIndex = itemIndex - this.bufferBefore[0].index;
                this.$body.insertBefore(item, this.bufferBefore[continueIndex].$node);
                this.bufferBefore.splice(continueIndex, 0, item);
                updateIndex(startIndex);
                this.viewIndex++;
                if ( this.bufferBefore.length > this.bufferSize ) {
                    item = this.bufferBefore.shift();
                    item.remove();
                }

                return;
            }
        } else {
            item = this.createItem(data, itemIndex);
            this.bufferBefore.unshift(item);
            this.insertBefore(item);
            updateIndex(0);
            this.viewIndex++;

            return;
        }
    }

    if (
        this.mainList[this.mainList.length - 1].index < itemIndex
        && (this.mainList.length >= this.size || (this.hasEmptyItems && this.mainList.length >= this.size - 1))
    ) {
        if ( this.bufferAfter.length ) {
            if ( this.bufferAfter[0].index + this.bufferSize <= itemIndex ) {
                return;
            }

            if ( this.bufferAfter[this.bufferAfter.length - 1].index >= itemIndex ) {
                item = this.createItem(data, itemIndex);
                continueIndex = itemIndex - this.bufferAfter[this.bufferAfter.length - 1].index;
                this.$body.insertBefore(item, this.bufferAfter[continueIndex].$node);
                this.bufferAfter.splice(continueIndex, 0, item);
            } else {
                item = this.createItem(data, itemIndex);
                this.add(item);
                this.bufferAfter.push(item);
            }

            if ( this.bufferAfter.length > this.bufferSize ) {
                item = this.bufferAfter.pop();
                item.remove();
            }

        } else {
            item = this.createItem(data, itemIndex);
            this.add(item);
            if ( this.mainList.length < this.size ) {
                item.show();
                this.mainList.push(item);
            } else {
                item.hide();
                this.bufferAfter.push(item);
            }
        }
        updateIndex(startIndex);
        this.checkClassStates();

        return;
    }

    item = this.createItem(data, itemIndex);
    item.show();
    continueIndex = itemIndex - this.mainList[0].index;
    if ( this.mainList.length > continueIndex ) {
        this.$body.insertBefore(item, this.mainList[continueIndex].$node);
        this.mainList.splice(continueIndex, 0, item);
    } else {
        this.add(item);
        this.mainList.push(item);
    }

    if ( itemIndex <= line.index ) {
        item = this.mainList.shift();
        item.hide();
        this.bufferBefore.push(item);
        updateIndex(startIndex);
        this.viewIndex++;
        if ( this.bufferBefore.length > this.bufferSize ) {
            item = this.bufferBefore.shift();
            item.remove();
        }
        this.focusToLine(this.mainList[this.focusPosition]);
    } else if ( this.mainList.length > this.size ) {
        item = this.mainList.pop();
        item.hide();
        this.bufferAfter.unshift(item);
        if ( this.bufferAfter.length > this.bufferSize ) {
            item = this.bufferAfter.pop();
            item.remove();
        }
    }

    updateIndex(startIndex);
    this.checkClassStates();
};


MainPanel.prototype.checkClassStates = function () {
    if ( this.hasEmptyItems ) {
        if ( this.focusLine.index < this.focusPosition ) {
            this.$node.classList.remove('noEmptyBefore');
        } else {
            this.$node.classList.add('noEmptyBefore');
        }
        if ( this.size <= this.mainList.length ) {
            this.$node.classList.remove('emptyAfter');
        } else {
            this.$node.classList.add('emptyAfter');
        }
    }

};


/**
 * Remove inner structures and HTML.
 *
 * @param {Object} data - data to remove
 */
MainPanel.prototype.removeItem = function ( data ) {
    var focus = this.focusLine === data.listLink,
        item = data.listLink,
        self = this,
        lastIndex = 0,
        startIndex = 0,
        itemIndex, dataIndex,
        newItem, delta, index;


    function updateIndex ( si ) {
        startIndex = 0;

        if ( si || si === 0 ) {
            startIndex = si;
        } else if ( self.bufferBefore.length ) {
            startIndex = self.bufferBefore[0].index;
        }

        for ( index = 0; index < self.bufferBefore.length; index++ ) {
            self.bufferBefore[index].index = startIndex;
            startIndex++;
        }

        for ( index = 0; index < self.mainList.length; index++ ) {
            self.mainList[index].index = startIndex;
            startIndex++;
        }

        for ( index = 0; index < self.bufferAfter.length; index++ ) {
            self.bufferAfter[index].index = startIndex;
            startIndex++;
        }
    }

    function fillBufferAfter () {
        delta = self.bufferSize - self.bufferAfter.length;
        for ( index = 0; index < delta; index++ ) {
            item = self.bufferAfter[self.bufferAfter.length - 1];
            if ( !item ) {
                break;
            }
            lastIndex = item.index;
            lastIndex++;
            if ( lastIndex >= self.data.length ) {
                break;
            }

            item = self.createItem(self.data[lastIndex], lastIndex);
            self.add(item);
            self.bufferAfter.push(item);
        }
    }

    dataIndex = this.data.indexOf(data);

    if ( dataIndex === -1 ) {
        return;
    }

    this.data.splice(dataIndex, 1);

    if ( !item ) {
        if ( this.bufferBefore.length ) {
            startIndex = this.bufferBefore[0].index;
        } else if ( this.mainList.length ) {
            startIndex = this.mainList[0].index;
        } else {

            return;
        }

        if ( startIndex < dataIndex && startIndex > 0 ) {
            this.viewIndex--;
            updateIndex(--startIndex);
            this.checkClassStates();
        }

        return;
    }

    itemIndex = this.bufferBefore.indexOf(item);
    if ( itemIndex !== -1 ) {
        lastIndex = this.bufferBefore[0].index;
        this.bufferBefore.splice(itemIndex, 1);
        item.remove();
        updateIndex();
        this.viewIndex--;

        return;
    }

    itemIndex = this.bufferAfter.indexOf(item);
    if ( itemIndex !== -1 ) {
        this.bufferAfter.splice(itemIndex, 1);
        item.remove();
        fillBufferAfter();
        updateIndex();
        if ( this.bufferBefore.length ) {
            lastIndex = this.bufferBefore[0].index;
        } else {
            lastIndex = 0;
        }
        if ( lastIndex > 0 ) {
            lastIndex--;
            newItem = this.createItem(this.data[lastIndex], lastIndex);
            newItem.hide();
            this.insertBefore(newItem);
            this.bufferBefore.unshift(newItem);
        }

        return;
    }

    itemIndex = this.mainList.indexOf(item);
    if ( itemIndex !== -1 ) {
        this.mainList.splice(itemIndex, 1);
        if ( itemIndex < this.focusPosition ) {
            if ( this.bufferBefore.length ) {
                if ( this.bufferBefore.length ) {
                    lastIndex = this.bufferBefore[0].index;
                } else {
                    lastIndex = 0;
                }
                newItem = this.bufferBefore.pop();
                this.mainList.unshift(newItem);
                newItem.show();
            }

            if ( lastIndex > 0 ) {
                this.viewIndex--;
                lastIndex--;
                newItem = this.createItem(this.data[lastIndex], lastIndex);
                newItem.hide();
                this.insertBefore(newItem);
                this.bufferBefore.unshift(newItem);
            }

        } else {
            if ( this.bufferAfter.length ) {
                newItem = this.bufferAfter.shift();
                this.mainList.push(newItem);
                newItem.show();
                fillBufferAfter();
            }

            if ( focus ) {
                if ( itemIndex < this.mainList.length ) {
                    this.focusToLine(this.mainList[itemIndex]);
                } else {
                    this.move(keys.up);
                }
            }
        }
        item.remove();
        updateIndex();
        this.checkClassStates();
    }
};


/**
 * Set data and render inner structures and HTML.
 *
 * @param {Object} config - init parameters (subset of constructor config params)
 */
MainPanel.prototype.setData = function ( config ) {
    // apply list of items

    if ( config.data ) {
        if ( DEVELOP ) {
            if ( !Array.isArray(config.data) ) { throw new Error(__filename + ': wrong config.data type'); }
        }

        // prepare user data
        this.data = config.data;
        this.$node.classList.remove('noEmptyBefore');
        this.$node.classList.remove('emptyAfter');
    }

    // reset current view window position
    this.viewIndex = null;


    if ( this.focusLine ) {
        this.blurLine(this.focusLine);
    }

    if ( config.noData ) {
        this.$noData.innerText = config.noData;
    }

    // set focus item
    if ( config.focusIndex !== undefined && this.data.length ) {
        if ( DEVELOP ) {
            if ( +config.focusIndex !== config.focusIndex ) {
                throw new Error(__filename + ': config.focusIndex must be a number');
            }
            if ( config.focusIndex < 0 ) {
                throw new Error(__filename + ': config.focusIndex should be positive');
            }
        }

        // jump to the necessary item
        this.focusIndex(config.focusIndex);
    } else {
        // go to the first page
        this.initView(0);
    }
};


/**
 * Init the visible window.
 *
 * @param {number} viewIndex - start position to render
 * @param {Function} callback - callback
 *
 * @return {boolean} operation state
 */
MainPanel.prototype.initView = function ( viewIndex, callback ) {
    var countBefore = 0,
        countAfter = 0,
        count = 0,
        stack = [],
        self = this,
        $item, initItems;

    if ( DEVELOP ) {
        if ( +viewIndex !== viewIndex ) {
            throw new Error(__filename + ': viewIndex must be a number');
        }
        if ( viewIndex < 0 ) {
            throw new Error(__filename + ': viewIndex should be more than zero');
        }
    }

    //TODO replace to global do async serial
    function finishCheck () {
        var index;

        for ( index = 0; index < stack.length; index++ ) {
            if ( countBefore > index ) {
                stack[index].hide();
                self.bufferBefore.push(stack[index]);
            } else if ( count > index ) {
                stack[index].show();
                self.mainList.push(stack[index]);
            } else {
                stack[index].hide();
                self.bufferAfter.push(stack[index]);
            }
        }
        stack = [];

        if ( index < self.data.length ) {
            // start caching data after get main data
            setTimeout(function () {
                function fillData ( list ) {
                    if ( list && list.length ) {
                        index++;
                        if ( index < self.data.length ) {
                            self.getItemData(self.data[index], fillData);
                        } else {
                            self.dataReady = true;
                        }
                    } else {
                        self.data.splice(index, 1);
                        if ( index < self.data.length ) {
                            self.getItemData(self.data[index], fillData);
                        } else {
                            self.dataReady = true;
                        }
                    }
                }

                self.getItemData(self.data[index], fillData);
            }, 500);
        } else {
            self.dataReady = true;
        }

        if ( callback ) {
            callback();
        }

        if ( self.mainList.length ) {
            self.$noData.classList.add('hidden');
            self.$body.classList.remove('hidden');
        } else {
            self.$body.classList.add('hidden');
            self.$noData.classList.remove('hidden');
        }
        self.emit('data:load');
    }

    function initItem () {
        $item = self.createItem(self.data[viewIndex], viewIndex);
        self.add($item);
        stack.push($item);
        viewIndex++;
        initItems();
    }

    initItems = function () {
        if ( self.data.length <= viewIndex || viewIndex >= countAfter ) {
            finishCheck();

            return;
        }

        if ( self.data[viewIndex].data ) {
            if ( self.data[viewIndex].data.length ) {
                initItem();
            } else {
                self.data.splice(viewIndex, 1);
                countAfter = self.data.length > countAfter ? countAfter : self.data.length;
                initItems();
            }
        } else if ( self.data[viewIndex].getter ) {
            self.data[viewIndex].getter(function ( listData ) {
                self.data[viewIndex].data = listData;
                if ( self.data[viewIndex].data.length ) {
                    initItem();
                } else {
                    self.data.splice(viewIndex, 1);
                    countAfter = self.data.length > countAfter ? countAfter : self.data.length;
                    initItems();
                }
            });
        } else {
            self.data.splice(viewIndex, 1);
            countAfter = self.data.length > countAfter ? countAfter : self.data.length;
            initItems();
        }
    };

    if ( this.viewIndex === viewIndex ) {

        return true;
    }

    while ( this.bufferBefore.length ) {
        this.bufferBefore[0].remove();
        this.bufferBefore.shift();
    }
    while ( this.mainList.length ) {
        this.mainList[0].remove();
        this.mainList.shift();
    }
    while ( this.bufferAfter.length ) {
        this.bufferAfter[0].remove();
        this.bufferAfter.shift();
    }

    this.viewIndex = viewIndex || 0;

    this.startIndex = this.viewIndex - this.bufferSize;
    if ( this.startIndex < 0 ) {
        count = Math.abs(this.startIndex);
        this.startIndex = 0;
    }
    viewIndex = this.startIndex;

    countBefore = this.bufferSize - count;
    count = countBefore + this.size;
    countAfter = (this.bufferSize * 2) + this.size;
    countAfter = this.data.length > countAfter ? countAfter : this.data.length;

    if ( this.startIndex === 0 && this.hasEmptyItems ) {
        if ( this.viewIndex > 0 && this.data.length > this.size ) {
            this.$node.classList.add('noEmptyBefore');
        }
        if ( this.viewIndex + this.size >= this.data.length ) {
            this.$node.classList.add('emptyAfter');
        }
        count--;
    }

    initItems();

    return false;
};


/**
 * Move focus to the given direction.
 *
 * @param {number} direction - arrow key code
 *
 * @fires module:stb/ui/list~List#overflow
 */
MainPanel.prototype.move = function ( direction ) {
    var item, index, delta,
        selectedIndex, lastIndex;

    if ( DEVELOP ) {
        if ( arguments.length !== 1 ) {
            throw new Error(__filename + ': wrong arguments number');
        }
        if ( +direction !== direction ) {
            throw new Error(__filename + ': direction must be a number');
        }
    }

    // empty list
    if ( !this.data.length || !this.focusLine ) {

        return;
    }
    switch ( direction ) {
        case keys.up:
            if ( this.focusLine.index > 0 ) {
                if ( this.bufferBefore.length  && this.focusLine.index <= this.viewIndex + this.focusPosition ) {
                    lastIndex = this.bufferBefore[0].index;
                    lastIndex--;
                    if ( this.hasEmptyItems ) {
                        this.$node.classList.remove('emptyAfter');
                    }
                    this.viewIndex--;
                    item = this.bufferBefore.pop();
                    if ( this.hasEmptyItems && this.focusLine.index === 1 ) {
                        this.$node.classList.remove('noEmptyBefore');
                    }
                    item.show();
                    this.mainList.unshift(item);
                    if ( this.size < this.mainList.length ) {
                        item = this.mainList.pop();
                        this.bufferAfter.unshift(item);
                        item.hide();
                    }
                    if ( this.bufferAfter.length > this.bufferSize ) {
                        item = this.bufferAfter.pop();
                        item.remove();
                    }
                    if ( lastIndex >= 0 ) {
                        item = this.createItem(this.data[lastIndex], lastIndex);
                        this.insertBefore(item);
                        this.bufferBefore.unshift(item);
                    }
                } else if ( this.hasEmptyItems ) {
                    this.$node.classList.remove('noEmptyBefore');
                }
                if ( this.focusLine.index > this.focusPosition ) {
                    selectedIndex = this.focusLine.index - this.viewIndex - 1;
                } else {
                    selectedIndex = this.focusLine.index - 1;
                }
                this.focusToLine(this.mainList[selectedIndex]);
            } else  if ( this.events['overflow'] ) {
                // notify listeners
                this.emit('overflow', {direction: direction, cycle: false});
            }
            break;
        case keys.down:
            if ( !this.dataReady  ) {

                return;
            }
            if ( this.focusLine.index < this.data.length - 1 ) {
                if ( this.hasEmptyItems && this.focusLine.index === this.focusPosition - 1 ) {
                    this.$node.classList.add('noEmptyBefore');
                    if ( this.bufferAfter.length ) {
                        item = this.bufferAfter.shift();
                        this.mainList.push(item);
                        item.show();
                    }
                    delta = this.bufferSize - this.bufferAfter.length;
                    for ( index = 0; index < delta; index++ ) {
                        item = this.bufferAfter[this.bufferAfter.length - 1];
                        if ( !item ) {
                            break;
                        }
                        lastIndex = item.index;
                        lastIndex++;
                        if ( lastIndex >= this.data.length ) {
                            break;
                        }

                        item = this.createItem(this.data[lastIndex], lastIndex);
                        this.add(item);
                        this.bufferAfter.push(item);
                    }
                } else if ( this.focusLine.index >= this.focusPosition && this.bufferAfter.length > 0 ) {
                    if ( this.hasEmptyItems && this.bufferAfter.length === 1 ) {
                        this.$node.classList.add('emptyAfter');
                    }
                    this.viewIndex++;
                    item = this.mainList.shift();
                    item.hide();
                    this.bufferBefore.push(item);
                    if ( this.bufferBefore.length > this.bufferSize ) {
                        item = this.bufferBefore.shift();
                        item.remove();
                    }
                    if ( this.size - this.mainList.length && this.bufferAfter.length ) {
                        item = this.bufferAfter.shift();
                        this.mainList.push(item);
                        item.show();
                    }
                    if ( this.bufferAfter.length && this.bufferSize > this.bufferAfter.length ) {
                        lastIndex = this.bufferAfter[this.bufferAfter.length - 1].index;
                        lastIndex++;
                        if ( lastIndex < this.data.length ) {
                            item = this.createItem(this.data[lastIndex], lastIndex);
                            this.add(item);
                            this.bufferAfter.push(item);
                        }
                    }
                } else if ( this.hasEmptyItems && !this.bufferAfter.length ) {
                    this.$node.classList.add('emptyAfter');
                    if ( this.mainList.length >= this.size ) {
                        this.viewIndex++;
                        item = this.mainList.shift();
                        item.hide();
                        this.bufferBefore.push(item);
                        if ( this.bufferBefore.length > this.bufferSize ) {
                            item = this.bufferBefore.shift();
                            item.remove();
                        }
                        if ( this.size - this.mainList.length && this.bufferAfter.length ) {
                            item = this.bufferAfter.shift();
                            this.mainList.push(item);
                            item.show();
                        }
                    }
                }
                selectedIndex = this.mainList[0].index - this.focusLine.index + 1;
                this.focusToLine(this.mainList[selectedIndex]);
            } else if ( this.events['overflow'] ) {
                // notify listeners
                this.emit('overflow', {direction: direction, cycle: false});
            }
            break;
    }
};


/**
 * Highlight the given DOM element as focused.
 * Remove focus from the previously focused item and generate associated event.
 *
 * @param {Element} $item - element to focus
 *
 * @return {boolean} operation status
 *
 * @fires module:stb/ui/list~List#focus:item
 * @fires module:stb/ui/list~List#blur:item
 */
MainPanel.prototype.focusToLine = function ( $item ) {
    var $prev = this.focusLine;

    if ( DEVELOP ) {
        if ( arguments.length !== 1 ) {
            throw new Error(__filename + ': wrong arguments number in focusLine');
        }
    }

    // different element
    if ( $item && $prev !== $item ) {
        if ( DEVELOP ) {
            if ( !($item instanceof Component) ) {
                throw new Error(__filename + ': wrong $item type');
            }
            if ( $item.$node.parentNode !== this.$body ) {
                throw new Error(__filename + ': wrong $item parent element');
            }
        }

        // some item is focused already
        if ( $prev !== null ) {
            if ( DEVELOP ) {
                if ( !($item instanceof Component) ) {
                    throw new Error(__filename + ': wrong $prev type');
                }
            }

            // style
            $prev.blur();
            //$prev.classList.remove('theme-focus');

            // there are some listeners
            if ( this.events['blur:line'] ) {
                /**
                 * Remove focus from an element.
                 *
                 * @event module:stb/ui/list~List#blur:item
                 *
                 * @type {Object}
                 * @property {Element} $item previously focused HTML element
                 */
                this.emit('blur:line', {$item: $prev});
            }
        }
        // reassign
        this.focusLine = $item;

        // this.focusLine.data = this.data[this.focusLine.index];

        // correct CSS
        if ( this.inFocus ) {
            $item.focus();

            this.inFocus = true;
            //$item.$node.classList.add('theme-focus');

            // there are some listeners
            if ( this.events['focus:line'] ) {
                /**
                 * Set focus to a DOM element.
                 *
                 * @event module:stb/ui/list~List#focus:item
                 *
                 * @type {Object}
                 * @property {Element} $prev old/previous focused HTML element
                 * @property {Element} $curr new/current focused HTML element
                 */
                this.emit('focus:line', {$prev: $prev, $curr: $item});
            }
        }

        return true;
    }

    // nothing was done
    return false;
};


/**
 * Highlight the given DOM element as blur.
 * Remove focus from the item and generate associated event.
 *
 * @param {Element} $item - element to focus
 *
 * @return {boolean} operation status
 *
 * @fires module:stb/ui/list~List#focus:item
 * @fires module:stb/ui/list~List#blur:item
 */
MainPanel.prototype.blurLine = function ( $item ) {
    if ( DEVELOP ) {
        if ( arguments.length !== 1 ) { throw new Error(__filename + ': wrong arguments number'); }
    }

    // different element
    if ( $item ) {
        if ( $item === this.focusLine ) {
            this.focusLine = null;
        }

        $item.blur();
        //$item.$node.classList.remove('theme-focus');

        // there are some listeners
        if ( this.events['blur:line'] ) {
            /**
             * Remove focus from an element.
             *
             * @event module:stb/ui/list~List#blur:item
             *
             * @type {Object}
             * @property {Element} $item previously focused HTML element
             */
            this.emit('blur:line', {$item: $item});
        }

        return true;
    }

    // nothing was done
    return false;
};

/**
 * Set the given item focused by item index.
 *
 * @param {number} index - item data index
 */
MainPanel.prototype.focusIndex = function ( index ) {
    var viewIndex = this.viewIndex || 0,
        self = this,
        tempIndex;

    if ( DEVELOP ) {
        if ( +index !== index ) {
            throw new Error(__filename + ': index must be a number');
        }
        if ( index < 0 ) {
            throw new Error(__filename + ': index should be positive');
        }
        // if ( index > this.data.length - 1 ) {
        //     throw new Error(__filename + ': index should be less than data size');
        // }
    }

    if ( index >= this.data.length ) {
        this.initView(0, function () {
            self.focusToLine(self.$body.firstChild);
        });
    } else if ( index >= viewIndex + this.focusPosition ) {
        viewIndex = index - this.focusPosition;
        if ( viewIndex + this.size < this.data.length ) {
            this.initView(viewIndex, function () {
                self.focusToLine(self.mainList[self.focusPosition]);
            });
        } else {
            tempIndex = this.size - this.data.length + index;
            viewIndex = this.data.length - this.size;
            if ( this.hasEmptyItems ) {
                tempIndex--;
                viewIndex++;
            }
            this.initView(viewIndex, function () {
                self.focusToLine(self.mainList[tempIndex]);
            });
        }

    } else {
        tempIndex = index - this.focusPosition;
        if ( tempIndex < 0 ) {
            this.initView(0, function () {
                self.focusToLine(self.mainList[index]);
            });
        } else {
            this.initView(tempIndex, function () {
                self.focusToLine(self.mainList[self.focusPosition]);
            });
        }
    }
};

// public
module.exports = MainPanel;
